import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useRegisterFormContext } from '../RegisterFormProvider';
import FormActions from './FormActions';
import translations from './translations';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { FormContext } from 'react-hook-form';

const Form = ({
  form,
  noValidate,
  autocomplete,
  renderActions,
  clearOnUnmount,
  children,
  component,
  cancelLabel,
  submitLabel,
}) => {
  const intl = useIntl();
  const showActions = !form.editInline || (form.editInline && form.isEditing);
  const { clear: clearForm } = form;
  const { formRef } = useRegisterFormContext();

  useEffect(() => {
    if (formRef) {
      formRef.current = form;
    }
  });

  useEffect(
    () => () => {
      if (formRef) {
        formRef.current = null;
      }
    },
    [formRef],
  );

  useEffect(
    () => () => {
      if (clearOnUnmount) {
        clearForm();
      }
    },
    [clearOnUnmount, clearForm],
  );

  const ComponentProp = component;
  const formProps = {};

  if (ComponentProp === 'form') {
    formProps.noValidate = noValidate;
    formProps.onSubmit = form.onSubmit;
    formProps.autoComplete = autocomplete;
  }

  return (
    <FormContext {...form}>
      <ComponentProp {...formProps}>
        {children}
        {showActions && renderActions && (
          <FormActions
            primaryAction={{
              label: submitLabel ?? intl.formatMessage(translations.formActions.save),
              onClick: form.onSubmit,
              type: 'submit',
              color: 'primary',
              variant: 'contained',
            }}
            secondaryAction={{
              label: cancelLabel ?? intl.formatMessage(translations.formActions.cancel),
              onClick: form.onCancel,
            }}
          />
        )}
      </ComponentProp>
    </FormContext>
  );
};

Form.propTypes = {
  form: PropTypes.object.isRequired,
  component: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  children: PropTypes.node.isRequired,
  renderActions: PropTypes.bool,
  clearOnUnmount: PropTypes.bool,
  autocomplete: PropTypes.string,
  noValidate: PropTypes.bool,
  cancelLabel: PropTypes.string,
  submitLabel: PropTypes.string,
};

Form.defaultProps = {
  component: 'form',
  renderActions: true,
  clearOnUnmount: true,
  autocomplete: 'off',
  noValidate: true,
  cancelLabel: undefined,
  submitLabel: undefined,
};

export default Form;
